import React from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { mdiPlus, mdiTrashCan, mdiContentSave } from '@mdi/js';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoData from '@/components/Formulario/CampoData';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import Grid, { GridItem } from '@/components/layout/Grid';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import AlertBox from '@/components/layout/AlertBox';

type Cupom = {
    id?: number;
    codigo?: string;
    tipo?: 'percentual' | 'valor';
    valor?: number;
    valido_de?: string | null;
    valido_ate?: string | null;
    limite_uso?: number | null;
    usos?: number;
    ativo?: boolean;
    catalogo_id?: number | null;
};

const VAZIO: Cupom = { codigo: '', tipo: 'percentual', valor: undefined, ativo: true };

const Cupons = ({ catalogos }: { catalogos: { id: number; nome: string }[] }) => {
    const [edicao, setEdicao] = React.useState<Cupom | null>(null);

    const query = useQuery(
        ['catalogo/cupom'],
        async () => (await axios.get('/api/catalogo/cupom')).data.result ?? [],
    );

    const salvar = useMutation(
        async (cupom: Cupom) =>
            cupom.id
                ? axios.put(`/api/catalogo/cupom/${cupom.id}`, cupom)
                : axios.post('/api/catalogo/cupom', cupom),
        {
            onSuccess: () => {
                toast('Cupom salvo', { type: 'success', position: 'top-center' });
                query.refetch();
                setEdicao(null);
            },
            onError: (error: any) =>
                toast(error.response?.data?.msg ?? 'Erro ao salvar cupom', {
                    type: 'error',
                    position: 'top-center',
                }),
        },
    );

    const excluir = useMutation(
        async (id: number) => axios.delete(`/api/catalogo/cupom/${id}`),
        {
            onSuccess: () => {
                toast('Cupom excluído', { type: 'success', position: 'top-center' });
                query.refetch();
            },
        },
    );

    const cabecalhos: DataTableHeader[] = [
        { label: 'Código', width: '20%', column: 'codigo' },
        {
            label: 'Desconto',
            width: '15%',
            render: (l: any) =>
                l.tipo === 'percentual' ? `${Number(l.valor)}%` : `R$ ${Number(l.valor).toFixed(2)}`,
        },
        {
            label: 'Validade',
            width: '20%',
            render: (l: any) =>
                l.valido_ate ? new Date(l.valido_ate).toLocaleDateString('pt-BR') : 'sem prazo',
        },
        {
            label: 'Usos',
            width: '12%',
            render: (l: any) => `${l.usos ?? 0}${l.limite_uso ? ` / ${l.limite_uso}` : ''}`,
        },
        { label: 'Ativo', width: '10%', render: (l: any) => (l.ativo ? 'Sim' : 'Não') },
        {
            label: 'Ações',
            width: '120px',
            render: (l: any) => (
                <HBox gap={8}>
                    <Botao variant="none-primary" title="Editar" icon={mdiContentSave} onClick={() => setEdicao(l)} />
                    <Botao variant="none-danger" title="Excluir" icon={mdiTrashCan} onClick={() => excluir.mutate(l.id)} />
                </HBox>
            ),
        },
    ];

    return (
        <VBox gap={16}>
            <AlertBox variant="info">
                <p>
                    Cupom e desconto por cliente <strong>não acumulam</strong>: numa cotação vale
                    o de maior valor. O contador de usos é incrementado quando o orçamento é
                    efetivamente emitido — rascunhos barrados pelo teto da IA não consomem.
                </p>
            </AlertBox>

            <HBox>
                <div style={{ flex: 1 }} />
                <Botao variant="primary" icon={mdiPlus} title="Novo cupom" onClick={() => setEdicao({ ...VAZIO })}>
                    Novo cupom
                </Botao>
            </HBox>

            {edicao && (
                <Grid>
                    <GridItem sm={12} md={3}>
                        <CampoTexto
                            instanceId="cupom-codigo"
                            label="Código"
                            value={edicao.codigo ?? ''}
                            onChange={(v: string) => setEdicao({ ...edicao, codigo: v.toUpperCase() })}
                            disabled={!!edicao.id}
                        />
                    </GridItem>
                    <GridItem sm={12} md={3}>
                        <CampoSeletor
                            instanceId="cupom-tipo"
                            label="Tipo"
                            options={[
                                { id: 'percentual', label: 'Percentual (%)' },
                                { id: 'valor', label: 'Valor fixo' },
                            ]}
                            value={edicao.tipo ?? 'percentual'}
                            onChange={(v: string | null) =>
                                setEdicao({ ...edicao, tipo: (v ?? 'percentual') as Cupom['tipo'] })
                            }
                        />
                    </GridItem>
                    <GridItem sm={12} md={3}>
                        <CampoNumero
                            instanceId="cupom-valor"
                            label="Desconto"
                            value={edicao.valor != null ? String(edicao.valor) : ''}
                            decimais={2}
                            somentePositivos
                            onChange={(v: string) => setEdicao({ ...edicao, valor: v ? Number(v) : undefined })}
                        />
                    </GridItem>
                    <GridItem sm={12} md={3}>
                        <CampoNumero
                            instanceId="cupom-limite"
                            label="Limite de usos"
                            value={edicao.limite_uso != null ? String(edicao.limite_uso) : ''}
                            decimais={0}
                            somentePositivos
                            onChange={(v: string) => setEdicao({ ...edicao, limite_uso: v ? Number(v) : null })}
                        />
                    </GridItem>

                    <GridItem sm={12} md={3}>
                        <CampoData
                            instanceId="cupom-de"
                            label="Válido de"
                            value={edicao.valido_de?.slice(0, 10) ?? ''}
                            onChange={(v: string) => setEdicao({ ...edicao, valido_de: v || null })}
                        />
                    </GridItem>
                    <GridItem sm={12} md={3}>
                        <CampoData
                            instanceId="cupom-ate"
                            label="Válido até"
                            value={edicao.valido_ate?.slice(0, 10) ?? ''}
                            onChange={(v: string) => setEdicao({ ...edicao, valido_ate: v || null })}
                        />
                    </GridItem>
                    <GridItem sm={12} md={3}>
                        <CampoSeletor
                            instanceId="cupom-catalogo"
                            label="Catálogo (vazio = todos)"
                            nullable
                            options={catalogos.map((c) => ({ id: String(c.id), label: c.nome }))}
                            value={edicao.catalogo_id ? String(edicao.catalogo_id) : null}
                            onChange={(v: string | null) =>
                                setEdicao({ ...edicao, catalogo_id: v ? Number(v) : null })
                            }
                        />
                    </GridItem>
                    <GridItem sm={12} md={3}>
                        <VBox gap={8}>
                            <CampoCheckbox
                                instanceId="cupom-ativo"
                                label="Ativo"
                                value={edicao.ativo !== false}
                                onChange={(v: boolean) => setEdicao({ ...edicao, ativo: v })}
                            />
                            <HBox gap={8}>
                                <Botao variant="primary" onClick={() => salvar.mutate(edicao)} disabled={salvar.isLoading}>
                                    Salvar
                                </Botao>
                                <Botao variant="none-primary" onClick={() => setEdicao(null)}>
                                    Cancelar
                                </Botao>
                            </HBox>
                        </VBox>
                    </GridItem>
                </Grid>
            )}

            <DataTable cabecalhos={cabecalhos} linhas={query.data ?? []} />
        </VBox>
    );
};

export default Cupons;
